import { Component, ViewEncapsulation, ViewChild, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

import { ProductService } from '../../../services/product.api.service';
import { EorderWizardStepperService } from '../../../services/eorder-wizard-stepper.service';
import { EorderWizardStep } from '../../../models/eorder-wizard-state';
import { EorderWizardService } from '../../../services/eorder-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { ProductA2c, ProductFamily } from '../../../models/eorder';
import {
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { ProductListComponent } from '../../utils/product-list/product-list.component';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';

@Component({
	selector: 'copo-eorder-wizard-stepprodspec',
	templateUrl: './step-prodspec.component.html',
	styleUrl: './step-prodspec.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		LoadingSpinnerComponent,
		TranslateModule,
		NgFor,
		MatIconModule,

		MatButtonModule,
		ProductListComponent,
		TitleCasePipe
	]
})
export class StepProdSpecComponent extends WizardPageContent implements OnInit, OnDestroy {
	@ViewChild('textSelectProductSpec', { read: ElementRef }) textSelectProductSpec?: ElementRef<HTMLElement>;
	productFamilies?: ProductFamily[];
	wasChecked = false;
	isLoading = true;
	isProductLoading = false;
	isDefault = false;
	selectedProductFamilies: string[] = [];
	soldToValue = '';
	productFamilySubscription?: Subscription;
	isCustomerNotSelected = true;
	loadDataSub: Subscription | undefined;
	productDetails = new Map<string, ProductA2c[]>();
	loadProductDataSub: Subscription | undefined;
	productFamily?: Subscription;
	products: ProductA2c[] | undefined;
	private readonly loadDataSubscription?: Subscription;
	private readonly VIEW_STEP_NUM = 0;

	constructor(
		private readonly appWizardService: EorderWizardService,
		readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly stepperService: EorderWizardStepperService,
		private readonly productService: ProductService,
	) {
		super(EorderWizardStep.ORDERINFORMATION, appWizardService.getState());
		this.productFamilySubscription = this.productService.familyProductClickEvent.subscribe(soldToVal => {
			this.soldToValue = soldToVal;
			this.getProductFamilies(soldToVal);
		});
		this.isLoading = false;
	}

	get isError() {
		return this.appWizardService.getState().errorInPage;
	}

	ngOnInit() {
		this.stepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
		const lastSelectedProductFamily = this.appWizardService.getState().ui.getSelectedProductFamily();
		if (!lastSelectedProductFamily) {
			return;
		}
		this.selectedProductFamilies = [lastSelectedProductFamily.code];
	}

	ngOnDestroy() {
		if (this.loadDataSubscription) {
			this.loadDataSubscription.unsubscribe();
		}
		if (this.productFamilySubscription) {
			this.productFamilySubscription.unsubscribe();
		}
		if (this.loadProductDataSub) {
			this.loadProductDataSub.unsubscribe();
		}
	}

	getProductFamilies(soldTo: string) {
		if (soldTo) {
			this.isCustomerNotSelected = false;
			this.isLoading = true;
			const productFamiliesInSession = this.appWizardService.getState().ui
				.getProductFamilies(soldTo);

			if (productFamiliesInSession && productFamiliesInSession.length > 0) {
				this.processProductFamilyResponse(soldTo, productFamiliesInSession);
			} else {
				this.loadDataSub = this.productService.getProductFamily(soldTo)
					.subscribe({
						next: productFamilies => {
							this.processProductFamilyResponse(soldTo, productFamilies);
						},
						error: (err: unknown) => {
							console.error('error loading products families', err);
							this.isLoading = false;
							this.appWizardService.getState().errorInPage = true;
						}
					});
			}
		}
	}

	processProductFamilyResponse(soldTo: string, productFamilies: ProductFamily[]) {
		this.productFamilies = productFamilies;
		this.appWizardService.getState().ui
			.setProductFamilies(soldTo, this.productFamilies);
		this.isLoading = false;
		this.appWizardService.getState().errorInPage = false;
		let selectedProductFamily = this.appWizardService.getState().ui.getSelectedProductFamily();
		if (this.productFamilies.length > 0) {
			if (this.appWizardService.getState().ui.getLastSelectedSoldTo() !== soldTo
				|| !selectedProductFamily) {
				selectedProductFamily = productFamilies[0];
				this.setProductFamily(productFamilies[0]);
			}
			this.appWizardService.getState().ui.setSoldTo(soldTo);
			this.familyProductChanged(selectedProductFamily.code);
			if (this.productFamilies.length === 1) {
				this.isDefault = true;
			}
		} else {
			this.familyProductChanged(undefined);
			this.isDefault = false;
		}
	}

	setProductFamily(productFamily: ProductFamily) {
		this.appWizardService.getState().ui
			.setSelectedProductFamily(productFamily);
	}

	checkError() {
		this.wasChecked = true;
		if (this.appWizardService.getStateEorderForm().getFormProducts().length === 0) {
			if (this.textSelectProductSpec) {
				this.textSelectProductSpec.nativeElement.scrollIntoView();
			}
			return true;
		}
		return false;
	}

	checkErrorForDraft() {
		return false;
	}

	getSelectedProductFamilyCode() {
		const selectedProductFamily = this.appWizardService.getState().ui.getSelectedProductFamily();
		return selectedProductFamily ? selectedProductFamily.code : undefined;
	}

	getSelectedProductFamilySubcategoryCode() {
		const selectedProductFamily = this.appWizardService.getState().ui.getSelectedProductFamily();
		return selectedProductFamily ? selectedProductFamily.subCategory : '';
	}

	getSelectedProductFamilyForm() {
		const selectedProductFamily = this.appWizardService.getState().ui.getSelectedProductFamily();
		return selectedProductFamily ? selectedProductFamily.form : '';
	}

	getRequestedProductCount() {
		return this.appWizardService.getStateEorderForm().getFormProducts().length;
	}

	familyProductChanged(selectedProductFamilyCode: string | undefined) {
		if (this.productFamilies && selectedProductFamilyCode) {
			this.appWizardService.getState().ui.setSelectedProductFamily(
				this.productFamilies.find(x => x.code === selectedProductFamilyCode)
			);
		} else {
			this.appWizardService.getState().ui.setSelectedProductFamily(undefined);
		}
		this.getProductFamilyTemplateDetails();
	}

	getProductFamilyTemplateDetails() {
		const selectedProductFamily = this.appWizardService.getState().ui.getSelectedProductFamily();
		if (selectedProductFamily !== undefined) {
			const key = `${ this.soldToValue }-${ selectedProductFamily.subCategory }-${ selectedProductFamily.form }`;
			this.isProductLoading = true;
			this.loadProductDataSub = forkJoin([
				this.getProductDetailsWithSubcatagoryAndForm(selectedProductFamily, this.soldToValue, key),
				this.getTemplateDetails(selectedProductFamily),
			]).subscribe({
				next: responseList => {
					if (responseList[0]) {
						this.productDetails.set(key, responseList[0]);
						this.products = responseList[0];
						this.selectedProductFamilies = [selectedProductFamily.code];
					}
					for (const templateDetail of responseList[1]) {
						this.appWizardService.getState().ui.updateTemplateNumberDetails(
							templateDetail.templateNumber,
							templateDetail
						);
					}
					this.isProductLoading = false;
				},
				error: (err: unknown) => {
					console.error('error loading products families', err);
					this.isProductLoading = false;
					this.appWizardService.getState().errorInPage = true;
				}
			});
		} else {
			this.selectedProductFamilies = [];
		}
	}

	getTemplateDetails(selectedProductFamily: ProductFamily) {
		const uniqueTemplateNum: number[] = [];
		for (const template of selectedProductFamily.customerTemplate) {
			if (template.customerTemplateNumber
				&& !this.appWizardService.getState().ui.hasTemlpateDetail(template.customerTemplateNumber)) {
				uniqueTemplateNum.push(template.customerTemplateNumber);
			}
		}
		const templateRequestList = uniqueTemplateNum
			.map(templateNumber => this.productService.getTemplateDetails(templateNumber));
		return templateRequestList.length > 0 ? forkJoin(templateRequestList) : of([]);
	}

	getProductDetailsWithSubcatagoryAndForm(selectedProductFamily: ProductFamily, soldTo: string, key: string) {
		return !this.productDetails.get(key) ? this.productService
			.getProductsDetails(selectedProductFamily.subCategory, selectedProductFamily.form, soldTo)
			: of(this.productDetails.get(key));
	}
}
